import { createI18n } from 'vue-i18n'
import { resolveWithKeyValue } from '@intlify/core-base'
import type { PluralizationRules } from '@intlify/core-base'
import { LanguageEnum } from '@apiTypes'

export const supportedLanguages = [LanguageEnum.En, LanguageEnum.De, LanguageEnum.Fr, LanguageEnum.Cs, LanguageEnum.Es]

export const defaultLanguage = supportedLanguages[0]

// Locale messages lazy-load — each becomes its own chunk fetched on first use.
// Glob pattern must be a string literal (Vite resolves it at parse time);
// keep in sync with supportedLanguages above. The dev-only check below catches drift.
const localeImporters = import.meta.glob<{ default: Record<string, string> }>('./locales/{en,de,fr,cs,es}.json')

if (import.meta.env.DEV) {
  const missing = supportedLanguages.filter(lang => !localeImporters[`./locales/${lang}.json`])
  if (missing.length > 0) {
    console.error(
      `[i18n] supportedLanguages and the locale glob have drifted. Missing locale JSON imports for: ${missing.join(', ')}. `
      + `Update the glob pattern in src/plugins/i18n/index.ts to include the new locale(s).`,
    )
  }
}

const loadedLocales = new Set<string>()

const numberFormats: Record<string, Record<string, Intl.NumberFormatOptions>> = {
  'en-US': {
    currency: {
      style: 'currency', currency: 'USD', notation: 'standard', minimumFractionDigits: 0, maximumFractionDigits: 2,
    },
    decimal: {
      style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2,
    },
    percent: {
      style: 'percent', useGrouping: false,
    },
  },
  'sk-SK': {
    currency: {
      style: 'currency',
      currency: 'EUR',
      useGrouping: true,
      currencyDisplay: 'symbol',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    },
    decimal: {
      style: 'decimal', minimumSignificantDigits: 2, maximumSignificantDigits: 2,
    },
    percent: {
      style: 'percent', useGrouping: false,
    },
  },
}

export function localeTitle(locale: string): string {
  switch (locale) {
    case 'en':
      return 'English'
    case 'sk':
      return 'Slovensky'
    case 'pl':
      return 'Polski'
    case 'cs':
      return 'Česky'
    case 'hu':
      return 'Magyar'
    case 'de':
      return 'Deutsch'
    case 'se':
      return 'Svenska'
    case 'it':
      return 'Italiano'
    case 'fr':
      return 'Français'
    case 'es':
      return 'Español'
    default:
      return locale
  }
}

export function localeFlag(locale: string): string {
  switch (locale) {
    case 'en':
      return 'gb'
    case 'cs':
      return 'cz'
    default:
      return locale
  }
}

const i18n = createI18n({
  legacy: false,
  locale: defaultLanguage,
  fallbackLocale: defaultLanguage,
  messages: {},
  numberFormats,
  fallbackWarn: false,
  missingWarn: false,
  messageResolver: (obj, path) => {
    const resolved = resolveWithKeyValue(obj, path)
    if (!resolved) return path

    // The unplugin-vue-i18n plugin pre-compiles messages into ASTs at build time.
    // Empty strings ("") compile to { body: { static: "", items: [] } }.
    // Detect these so untranslated keys fall back to the English key with interpolation.
    if (typeof resolved === 'object') {
      const body = (resolved as { body?: { static?: string; items?: unknown[] } }).body
      if (body?.static === '' && !body.items?.length) return path
    }

    return resolved
  },
  pluralRules: pluralizationRules(),
})

export default i18n

// Tracks the most-recent loadLocale request so a slow/late chunk can't override
// a newer one (e.g. de fetched then fr fetched — if de resolves last, ignore it).
let activeLocaleRequest = 0

/**
 * Lazy-load and activate a locale. Idempotent — calling with an already-loaded
 * locale just sets the active value. Falls back silently for unsupported locales
 * and chunk-load failures (so callers using `void loadLocale(x)` don't blow up
 * on transient network errors).
 */
export async function loadLocale(locale: string): Promise<void> {
  if (!isLanguageSupported(locale)) return

  const requestId = ++activeLocaleRequest

  if (loadedLocales.has(locale)) {
    if (requestId === activeLocaleRequest && i18n.global.locale.value !== locale)
      i18n.global.locale.value = locale

    return
  }

  const importer = localeImporters[`./locales/${locale}.json`]
  if (!importer) return

  try {
    const module = await importer()
    if (requestId !== activeLocaleRequest) return
    i18n.global.setLocaleMessage(locale, module.default)
    loadedLocales.add(locale)
    i18n.global.locale.value = locale
  }
  catch (error) {
    console.error(`[i18n] Failed to load locale "${locale}"`, error)
  }
}

export function parseDefaultLocale(): string {
  const browserLanguage = window.navigator.language.split('-')[0]

  return isLanguageSupported(browserLanguage) ? browserLanguage : defaultLanguage
}

function pluralizationRules(): PluralizationRules {
  return supportedLanguages.reduce((rules, locale) => ({
    ...rules, [locale]: pluralizationRule,
  }), {})
}

function pluralizationRule(choice: number): number {
  if (choice <= 1) return choice

  return choice <= 4 ? 2 : 3
}

export function isLanguageSupported(locale: string | undefined): locale is LanguageEnum {
  return !!locale && supportedLanguages.some(supported => supported === locale)
}
